import { CheckOutlined } from '@ant-design/icons'
import React from 'react'

/**
 * StegRad – processlinjen högst upp i besöksflödena.
 *
 * Ersätter Ant Designs <Steps>, som lade varje etikett PÅ SAMMA RAD som sin siffra
 * och klippte det som inte fick plats. På en platta i porträtt stod det
 * "Behovsfråg" och "Prioriterinç" – sex steg i bredd ryms helt enkelt inte.
 *
 * Här ligger etiketten i stället UNDER siffran, i en kolumn som delar bredden lika
 * med de andra. Varje namn får hela kolumnen och radbryts i värsta fall, men klipps
 * aldrig. Inga fasta bredder någonstans: raden fungerar lika bra med sidomenyn
 * utfälld som hopfälld, och tål att ett steg tillkommer.
 *
 * Färgerna är Ant Designs egna (#1890ff, #d9d9d9) och siffrorna ser ut precis som
 * förut – det är bara etiketterna som flyttat ned.
 *
 * Raden är avsiktligt inte klickbar. Stegen har villkor för att få lämnas, och de
 * villkoren sitter i knappraden längst ned.
 */

const BLA = '#1890ff'
const GRA = '#d9d9d9'

export interface StegRadProps {
  /** Stegens namn i ordning. Längden avgör hur många kolumner som ritas. */
  steg: string[]
  /** Nollindexerat – steget som pågår just nu. */
  aktivt: number
}

const StegRad: React.FC<StegRadProps> = ({ steg, aktivt }) => (
  // Luften nedåt är medvetet rejäl: etiketterna hänger under siffrorna och skulle
  // annars binda ihop sig med rubriken på det steg man står i.
  <ol className="flex list-none" style={{ margin: '0 0 32px', padding: 0 }}>
    {steg.map((namn, i) => {
      const klar = i < aktivt
      const aktiv = i === aktivt
      return (
        <li
          key={namn}
          aria-current={aktiv ? 'step' : undefined}
          className="relative flex flex-1 flex-col items-center text-center"
        >
          {/* Linjen bakåt till föregående siffra. 20px = cirkelns radie plus luft, så
              att den varken nuddar den här siffran eller den förra. */}
          {i > 0 && (
            <span
              aria-hidden
              className="absolute"
              style={{
                top: 12,
                left: 'calc(-50% + 20px)',
                right: 'calc(50% + 20px)',
                height: 1,
                background: klar ? BLA : GRA,
              }}
            />
          )}

          <span
            className="relative flex items-center justify-center rounded-full border"
            style={{
              width: 24,
              height: 24,
              fontSize: 12,
              borderColor: klar || aktiv ? BLA : GRA,
              background: aktiv ? BLA : '#fff',
              color: aktiv ? '#fff' : klar ? BLA : 'rgba(0, 0, 0, 0.45)',
            }}
          >
            {klar ? <CheckOutlined /> : i + 1}
          </span>

          <span
            className="mt-1 px-1 leading-snug"
            style={{ fontSize: 13, fontWeight: aktiv ? 600 : 400, color: aktiv ? '#262626' : '#8c8c8c' }}
          >
            {namn}
          </span>
        </li>
      )
    })}
  </ol>
)

export default StegRad
